import { ActionIcon, Badge, Box, Group, Switch, Table, UnstyledButton } from "@mantine/core";

import { IconEdit } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { getProcedureCodeEffectiveLabel } from "@/shared/procedure-code-effective-dates";

import { getColumnConfig } from "../constants/fee-table";
import { ProcedureCode } from "../types/fee-table";

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `$${value.toLocaleString()}`;
}

interface ProcedureRowProps {
  procedure: ProcedureCode;
  onEdit: (procedure: ProcedureCode) => void;
  onInfo: (procedure: ProcedureCode) => void;
  onStatusToggle: (procedure: ProcedureCode, isActive: boolean) => void;
  isUpdating?: boolean;
  readOnly?: boolean;
}

export default function ProcedureRow({
  procedure,
  onEdit,
  onInfo,
  onStatusToggle,
  isUpdating = false,
  readOnly = false,
}: ProcedureRowProps) {
  const statusCol = getColumnConfig("status");
  const codeCol = getColumnConfig("code");
  const nameCol = getColumnConfig("name");
  const insuranceFeeCol = getColumnConfig("insuranceFee");
  const selfPayFeeCol = getColumnConfig("selfPayFee");
  const actionCol = getColumnConfig("action");
  const effectiveLabel = getProcedureCodeEffectiveLabel(procedure.code);

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onStatusToggle(procedure, event.currentTarget.checked);
  };

  return (
    <Table.Tr data-testid={`fee-row-${procedure.code}`}>
      <Table.Td colSpan={statusCol.colSpan}>
        <Switch
          checked={procedure.isActive}
          onChange={handleStatusChange}
          disabled={readOnly || isUpdating}
          aria-label={`Toggle ${procedure.code} status`}
          label={procedure.isActive ? "Active" : "Inactive"}
          data-testid={`fee-row-status-toggle-${procedure.code}`}
        />
      </Table.Td>
      <Table.Td colSpan={codeCol.colSpan}>
        <TextWrapper>{procedure.code}</TextWrapper>
      </Table.Td>
      <Table.Td colSpan={nameCol.colSpan}>
        <Group gap="xs">
          <TextWrapper>{procedure.name}</TextWrapper>
          {effectiveLabel ? (
            <Badge
              fz="sm"
              data-testid={`fee-row-effective-badge-${procedure.code}`}
              leftSection={<Box w={6} h={6} bg="blue.6" style={{ borderRadius: "50%" }} />}
              styles={{
                root: { backgroundColor: "var(--mantine-color-blue-1)" },
                label: { color: "var(--mantine-color-dark-9)" },
              }}
            >
              {effectiveLabel}
            </Badge>
          ) : null}
          <UnstyledButton
            onClick={() => onInfo(procedure)}
            data-testid={`fee-row-info-button-${procedure.code}`}
            aria-label={`View info for ${procedure.code} - ${procedure.name}`}
          >
            <TextWrapper variant="bold" td="underline">
              Info
            </TextWrapper>
          </UnstyledButton>
        </Group>
      </Table.Td>
      <Table.Td ta={insuranceFeeCol.align} colSpan={insuranceFeeCol.colSpan}>
        <TextWrapper>{formatCurrency(procedure.insuranceFee)}</TextWrapper>
      </Table.Td>
      <Table.Td ta={selfPayFeeCol.align} colSpan={selfPayFeeCol.colSpan}>
        <TextWrapper>{formatCurrency(procedure.selfPayFee)}</TextWrapper>
      </Table.Td>
      <Table.Td ta={actionCol.align} colSpan={actionCol.colSpan}>
        {readOnly ? null : (
          <ActionIcon
            color="gray"
            variant="subtle"
            onClick={() => onEdit(procedure)}
            aria-label={`Edit ${procedure.code} fees`}
            data-testid={`fee-row-edit-button-${procedure.code}`}
          >
            <IconEdit size={18} />
          </ActionIcon>
        )}
      </Table.Td>
    </Table.Tr>
  );
}
